/* MoviePalette cookie consent — replaces the Consentik app ($6.99/mo).
 *
 * WHAT IT DOES, and why each part exists
 * --------------------------------------
 * 1. Shows a banner only where consent is legally required. Region comes from
 *    Shopify's own `shouldShowBanner()` — Shopify already knows the visitor's
 *    country and which privacy regime applies (GDPR / CPRA / etc). We never
 *    geolocate ourselves and never call a third-party IP service, which is what
 *    the old stack did via get.geojs.io.
 *
 * 2. Records the choice with Shopify's Customer Privacy API
 *    (`setTrackingConsent`). This is the part that legally matters on Shopify:
 *    it gates Shopify's own pixels, the Web Pixels API, and app pixels. Storing a
 *    cookie ourselves would NOT do that.
 *
 * 3. Mirrors the choice into Google Consent Mode v2 via gtag('consent', ...).
 *    The store runs Google Ads (AW-465510045) and GA4 (G-YG13RFH71P) directly.
 *    Without these signals Google degrades EEA conversion measurement — which
 *    matters because the consent log shows traffic is heavily UK/DE/FR/NL/PL/ES/IT.
 *
 * ORDER IS LOAD-BEARING: the `default` consent state must be pushed BEFORE any
 * Google tag fires. That is done inline in the theme head (see mp-consent.liquid),
 * not here — by the time this file runs, gtag may already have queued events.
 *
 * NOT INCLUDED, deliberately: no jQuery (the old banner shipped its own copy on
 * top of the two the theme already had), no auto-blocking of third-party scripts,
 * no IAB TCF. Those were all switched off in the app being replaced.
 */
(function () {
  'use strict';

  var CFG = window.MP_CONSENT || {};
  var STORE_KEY = 'mp_consent_v1';

  function gtag() { window.dataLayer = window.dataLayer || []; window.dataLayer.push(arguments); }

  /* ---------- persistence (our own copy, for re-opening preferences) ---------- */

  function save(state) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ t: Date.now(), s: state })); } catch (e) {}
  }
  function load() {
    try { return (JSON.parse(localStorage.getItem(STORE_KEY) || 'null') || {}).s || null; } catch (e) { return null; }
  }

  /* ---------- apply a decision everywhere it needs to go ---------- */

  function apply(state) {
    // 1. Google Consent Mode v2
    gtag('consent', 'update', {
      ad_storage: state.marketing ? 'granted' : 'denied',
      ad_user_data: state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied',
      analytics_storage: state.analytics ? 'granted' : 'denied',
      functionality_storage: state.preferences ? 'granted' : 'denied',
      personalization_storage: state.preferences ? 'granted' : 'denied',
      security_storage: 'granted'
    });

    // 2. Shopify Customer Privacy API — gates Shopify + app pixels
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (api && api.setTrackingConsent) {
      api.setTrackingConsent({
        analytics: !!state.analytics,
        marketing: !!state.marketing,
        preferences: !!state.preferences,
        sale_of_data: !!state.marketing
      }, function () {});
    }

    save(state);
    window.dispatchEvent(new CustomEvent('mp:consent', { detail: state }));
  }

  var ALL = { analytics: true, marketing: true, preferences: true };
  var NONE = { analytics: false, marketing: false, preferences: false };

  /* Deny at Shopify's level the moment we know a banner is required.
   *
   * WHY THIS IS NECESSARY (measured live 2026-08-10): Settings > Customer privacy has
   * no region config, so Shopify believes consent is not required and reports
   * analyticsProcessingAllowed() === marketingAllowed() === true before the visitor
   * has answered. Shopify's Web Pixel then pushes its OWN
   * gtag('consent','default',{...granted}) a moment after ours, overriding the denied
   * default and letting Google tags fire behind the banner.
   *
   * Setting Shopify's consent to false up front makes its pixel emit denied instead,
   * so both defaults agree. The visitor's actual choice then updates both.
   *
   * Turning on privacy automation in admin would fix this at source, but it also
   * installs Shopify's own banner — declined by the owner — so this is the code-side
   * equivalent.
   */
  function denyUntilAnswered() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || !api.setTrackingConsent) return;
    try {
      api.setTrackingConsent({
        analytics: false, marketing: false, preferences: false, sale_of_data: false
      }, function () {});
    } catch (e) {}
  }

  /* ---------- ui ---------- */

  var el = null;

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function close() { if (el) { el.remove(); el = null; } }

  function render(showPrefs) {
    close();
    var prev = load() || { analytics: true, marketing: true, preferences: true };
    el = document.createElement('div');
    el.className = 'mpc';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-label', 'Cookie preferences');

    var toggles = [
      ['necessary', 'Necessary', true, true],
      ['preferences', 'Preferences', prev.preferences, false],
      ['marketing', 'Marketing', prev.marketing, false],
      ['analytics', 'Analytics', prev.analytics, false]
    ].map(function (t) {
      return '<label class="mpc-t' + (t[3] ? ' mpc-t--locked' : '') + '">' +
        '<input type="checkbox" data-k="' + t[0] + '"' + (t[2] ? ' checked' : '') +
        (t[3] ? ' disabled' : '') + '>' +
        '<span class="mpc-sw" aria-hidden="true"></span>' +
        '<span class="mpc-tl">' + t[1] + '</span></label>';
    }).join('');

    el.innerHTML =
      '<div class="mpc-box">' +
        '<p class="mpc-h">' + esc(CFG.title || 'We respect your privacy') + '</p>' +
        '<p class="mpc-p">' + esc(CFG.body || 'This website uses cookies to ensure you get the best experience on our website.') + '</p>' +
        '<div class="mpc-toggles"' + (showPrefs ? '' : ' hidden') + '>' + toggles + '</div>' +
        (CFG.policyUrl ? '<a class="mpc-link" href="' + esc(CFG.policyUrl) + '">Privacy Policy</a>' : '') +
        '<div class="mpc-actions">' +
          '<button type="button" class="mpc-b mpc-b--ghost" data-a="' + (showPrefs ? 'save' : 'prefs') + '">' +
            (showPrefs ? 'Save my choice' : 'Preferences') + '</button>' +
          '<button type="button" class="mpc-b mpc-b--ghost" data-a="decline">Decline</button>' +
          '<button type="button" class="mpc-b mpc-b--primary" data-a="accept">Got it!</button>' +
        '</div>' +
      '</div>';

    el.addEventListener('click', function (e) {
      var a = e.target.getAttribute && e.target.getAttribute('data-a');
      if (!a) return;
      if (a === 'prefs') { render(true); return; }
      if (a === 'accept') { apply(ALL); close(); return; }
      if (a === 'decline') { apply(NONE); close(); return; }
      if (a === 'save') {
        var s = { analytics: false, marketing: false, preferences: false };
        el.querySelectorAll('input[data-k]').forEach(function (i) {
          if (i.dataset.k !== 'necessary') s[i.dataset.k] = i.checked;
        });
        apply(s); close();
      }
    });

    document.body.appendChild(el);
  }

  /* ---------- boot ---------- */

  /* FAIL TOWARDS SHOWING THE BANNER.
   *
   * The first version gated the banner on Shopify's shouldShowBanner(), which reads
   * the store's Settings > Customer privacy region config. That config had never been
   * set up here — Consentik did its own geolocation for years — so shouldShowBanner()
   * returned false for everyone and a visitor in Lithuania saw no banner at all.
   *
   * Showing a banner to someone who didn't strictly need one is harmless. NOT showing
   * one to an EU visitor is a compliance failure. So the default is now: no saved
   * choice => show it, everywhere. Region gating is opt-in via MP_CONSENT.regionOnly,
   * and should only be turned on once Settings > Customer privacy is actually
   * configured and verified from an EU IP.
   *
   * Same reasoning for the API: if Shopify's privacy API never loads we still show the
   * banner and still push the gtag signals. Consent Mode defaults stay denied, so
   * nothing tracks in the meantime.
   */
  /* Countries where a consent banner is required before non-essential cookies.
     EU 27 + EEA (Iceland, Liechtenstein, Norway) + UK + Switzerland.
     Deliberately NOT the USA: there is no federal opt-in requirement, and the
     state laws that do apply (California, Delaware, Florida...) are opt-OUT —
     served by the Data sharing opt-out page in Shopify admin, not by a banner. */
  var CONSENT_REQUIRED = ('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL ' +
    'PL PT RO SK SI ES SE IS LI NO GB CH').split(' ');

  /* Visitor country from Shopify's own IP detection:
     GET /browsing_context_suggestions.json -> detected_values.country.handle.
     First-party (the shop's own domain), needs no admin privacy config, no
     third-party geolocation (the old stack called get.geojs.io on every page).

     NOT used: Liquid's localization.country — that is the visitor's assigned
     MARKET (pricing region), not where they are; a Lithuanian visitor on a store
     whose only market is the US would report "US" and silently lose the banner.

     Failure policy: unknown, timeout (1.5s) or error => SHOW the banner. A
     spurious banner outside the EU is harmless; a missing one inside it is not.
     ?mpc_country=XX overrides detection, for testing either path from anywhere. */
  function detectCountry(cb) {
    var m = location.search.match(/[?&]mpc_country=([A-Za-z]{2})/);
    if (m) { cb(m[1].toUpperCase()); return; }
    var cached = null;
    try { cached = sessionStorage.getItem('mp_geo'); } catch (e) {}
    if (cached) { cb(cached); return; }
    var done = false;
    function finish(c) {
      if (done) return;
      done = true;
      if (c) { try { sessionStorage.setItem('mp_geo', c); } catch (e) {} }
      cb(c);
    }
    setTimeout(function () { finish(null); }, 1500);
    fetch('/browsing_context_suggestions.json', { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var c = d && d.detected_values && d.detected_values.country &&
                d.detected_values.country.handle;
        finish(c ? String(c).toUpperCase() : null);
      })
      .catch(function () { finish(null); });
  }

  function start() {
    var saved = load();
    if (saved) { apply(saved); return; }    // re-assert the stored choice on every page

    detectCountry(function (c) {
      if (c && CONSENT_REQUIRED.indexOf(c) === -1) {
        // Known non-consent country (e.g. US): no banner; grant so ads/analytics
        // work normally. The opt-OUT route for US states is Shopify's
        // data-sharing page, not this banner.
        apply(ALL);
        return;
      }
      // Consent country, or unknown: pin Shopify to denied BEFORE showing the
      // banner, so its Web Pixel cannot grant on our behalf while the visitor reads.
      denyUntilAnswered();
      render(false);
    });
  }

  function boot() {
    // Ask Shopify to load its consent API, but never let that gate the banner.
    try {
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }], function () {});
      }
    } catch (e) {}
    start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // let a footer link re-open preferences (required: consent must be withdrawable)
  window.MPConsent = { open: function () { render(true); }, get: load };
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-mp-consent-open]');
    if (t) { e.preventDefault(); render(true); }
  });
})();
